import type { BlockContentAndMedia } from 'types/block-content-and-media';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { usePostHog } from 'posthog-js/react';
import dynamic from 'next/dynamic';
import { Button, Wrap } from '@leafwell/components';
import HeadingBody from './heading-body';
import WysiwygContent from './wysiwyg-content';
import Figure from './figure';
import { useEventsTracker } from 'contexts/eventsTracker';
import { toQueryString } from 'lib/utils/toQueryString';

const VideoYoutube = dynamic(() => import('./video-youtube'));

const ContentAndMedia: React.FC<BlockContentAndMedia> = ({
  sectionId,
  instanceId,
  title,
  label,
  content,
  contentAlignment,
  button,
  image,
  youtubeVideoId,
  videoThumbnail,
  playButtonPosition = 'center',
  mediaLabel,
}) => {
  const { trackEvent } = useEventsTracker();
  const { asPath, query } = useRouter();
  const posthog = usePostHog();
  const sessionId = posthog.get_session_id();
  const distinctId = posthog.get_distinct_id();

  const product: 'membership' | 'telepharmacist' | null = asPath.includes(
    '/membership',
  )
    ? 'membership'
    : asPath.includes('/cannabis-care')
    ? 'telepharmacist'
    : null;
  const [newUrl, setNewUrl] = useState<string | null>(null);
  const queryParams = toQueryString(query);

  useEffect(() => {
    if (product) {
      setNewUrl(
        button?.url?.includes('?')
          ? `${button.url}&session_id=${sessionId}&distinct_id=${distinctId}${
              queryParams ? `&${queryParams}` : ''
            }`
          : `${button.url}?session_id=${sessionId}&distinct_id=${distinctId}${
              queryParams ? `&${queryParams}` : ''
            }`,
      );
    }
  }, [sessionId, distinctId, product, queryParams]);

  const leftAlignment = contentAlignment === 'left';

  return (
    <Wrap id={sectionId} itemID={`ContentAndMedia-${instanceId}`}>
      {label?.length > 0 ? (
        <div className="grid gap-y-6 justify-items-center">
          <p className="text-xl max-w-3xl text-center text-gray-700">{label}</p>
        </div>
      ) : null}
      <div
        className={[
          'grid gap-x-32 gap-y-6',
          image
            ? 'lg:grid-cols-auto-auto lg:items-center'
            : 'lg:grid-cols-2 lg:items-start',
        ].join(' ')}
      >
        <div className={['grid gap-y-8 max-w-2xl'].join(' ')}>
          {title?.length > 0 && (
            <HeadingBody
              className="font-semibold text-black"
              level="2"
              title={title}
            />
          )}
          {content?.length > 0 ? (
            <WysiwygContent
              content={content}
              className="leafwell-add-margin-top-to-li !text-gray-700"
            />
          ) : null}
          {button && (
            <Button
              href={product ? newUrl : button.url}
              label={button.title}
              rounded={true}
              filled={true}
              variant="secondary"
              target={button.target}
              className="w-full md:w-fit h-fit"
              onClick={() => {
                trackEvent({
                  event: 'select_content',
                  content_type: button.title,
                  item_id: `ContentAndMedia-${instanceId}`,
                  action: 'ContentAndMedia-click-cta',
                });
                if (product) {
                  trackEvent({
                    event: 'generate_lead',
                    item_id: `ContentAndMedia-${instanceId}`,
                    service: product,
                    page_referrer: document.referrer,
                  });
                }
              }}
            />
          )}
        </div>
        <div
          className={[
            'order-first',
            !leftAlignment ? 'md:order-first' : 'md:order-last',
          ].join(' ')}
        >
          {image && <Figure {...image} />}
          {youtubeVideoId && (
            <VideoYoutube
              title=""
              content=""
              youtubeVideos={Array({
                youtubeVideoId: youtubeVideoId,
                image: videoThumbnail,
                playButtonStyle:
                  playButtonPosition === 'center' ? 'secondary' : 'tertiary',
              })}
              videoSize="large"
              variant="none"
              sectionId=""
              removePadding={true}
            />
          )}
          {mediaLabel?.length > 0 ? (
            <p
              className="text-sm mt-4"
              dangerouslySetInnerHTML={{ __html: mediaLabel }}
            />
          ) : null}
        </div>
      </div>
    </Wrap>
  );
};

export default ContentAndMedia;
